import type React from "react";
import { createContext, useContext, useReducer } from "react";

type ModalSizes = "small" | "medium" | "large";

type Action = { type: "openModal"; payload: State } | { type: "closeModal" };
type Dispatch = (action: Action) => void;

type State = {
  size: ModalSizes;
  component: React.ReactNode;
};

type ProviderProps = {
  children: React.ReactNode;
};

const initialModalState: State = {
  component: null,
  size: "medium",
};

export const modalReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "openModal": {
      return { ...state, ...action.payload };
    }
    case "closeModal": {
      return { ...state, component: null };
    }

    default: {
      throw new Error(`Unhandled action type: ${JSON.stringify(action)}`);
    }
  }
};

const ModalContext = createContext<State | undefined>(undefined);
const ModalUpdaterContext = createContext<Dispatch | undefined>(undefined);

const ModalProvider = ({ children }: ProviderProps) => {
  const [state, dispatch] = useReducer(modalReducer, initialModalState);
  return (
    <ModalContext.Provider value={state}>
      <ModalUpdaterContext.Provider value={dispatch}>{children}</ModalUpdaterContext.Provider>
    </ModalContext.Provider>
  );
};

/**
 * Exports
 */

export const useModalState = () => {
  const modalState = useContext(ModalContext);
  if (typeof modalState === "undefined") {
    throw new Error("useModalState must be used within a ModalProvider");
  }
  return modalState;
};

export const useModal = () => {
  const dispatch = useContext(ModalUpdaterContext);
  if (typeof dispatch === "undefined") {
    throw new Error("useModal must be used within a ModalProvider");
  }

  const openModal = (component: React.ReactNode, size: ModalSizes) => {
    dispatch({ type: "openModal", payload: { component, size } });
  };

  const closeModal = () => {
    dispatch({ type: "closeModal" });
  };

  return { openModal, closeModal };
};
export default ModalProvider;
